import { useCallback, useEffect, useRef, useState } from "react";

interface UseScrollHeaderOptions {
  mobileOnly?: boolean;
  topThreshold?: number;
  scrollUpThreshold?: number;
}

export function useScrollHeader({
  mobileOnly = true,
  topThreshold = 20,
  scrollUpThreshold = 80,
}: UseScrollHeaderOptions = {}) {
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const scrollUpAmount = useRef(0);

  useEffect(() => {
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Scroll tracking requires complex state management for different screen sizes
    const handleScroll = () => {
      // Only apply this behavior on mobile if mobileOnly is true
      if (mobileOnly && window.innerWidth >= 768) {
        setIsHeaderVisible(true);
        return;
      }

      const currentScrollY = window.scrollY;

      // Exception: Always show header when near the top (within topThreshold)
      if (currentScrollY <= topThreshold) {
        setIsHeaderVisible(true);
        lastScrollY.current = currentScrollY;
        return;
      }

      const scrollDelta = currentScrollY - lastScrollY.current;

      if (scrollDelta > 0) {
        // Scrolling down - hide header immediately
        setIsHeaderVisible(false);
        // Reset the scroll up counter when direction changes
        scrollUpAmount.current = 0;
      } else if (scrollDelta < 0) {
        // Scrolling up - accumulate scroll up amount
        scrollUpAmount.current += Math.abs(scrollDelta);

        // Only show header after scrolling up at least scrollUpThreshold px
        if (scrollUpAmount.current > scrollUpThreshold) {
          setIsHeaderVisible(true);
        }
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [mobileOnly, topThreshold, scrollUpThreshold]);

  const resetHeaderVisibility = useCallback(() => {
    setIsHeaderVisible(true);
    lastScrollY.current = 0;
    scrollUpAmount.current = 0;
  }, []);

  return { isHeaderVisible, resetHeaderVisibility };
}
